import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import { VAutocompleteComponent } from "@app/directives/_forms/v-autocomplete/v-autocomplete.component";
import { IPill } from "@app/directives/_veri/v-pills/v-pills.component";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { IKeyValuePair } from "@app/interfaces/IKeyValuePair";
import { GroupService } from "@app/services/groups/group.service";
import { VehicleService } from "@app/services/vehicle.service";
import { IGroup } from "../../groups/_models/IGroup";
import { IDrivingStyle, IDrivingStyleAssociation } from "../driving-styles.interfaces";
import { DrivingStylesService } from "../driving-styles.service";
import './driving-style-associations-manager.component.scss';

enum SelectionType {
    Group,
    VehicleType
}

interface IDrivingStyleAssociationDisplay {
    title: string;
    description: string;
    association: IDrivingStyleAssociation;
    icon: string;
}

@Component({
    selector: 'driving-style-associations-manager',
    template: require('./driving-style-associations-manager.component.html')
})
export class DrivingStyleAssociationsManagerComponent implements OnInit, OnChanges {

    @Input() existingDrivingStyles: IDrivingStyle[];
    @Input() drivingStyle: IDrivingStyle;
    @Output() drivingStyleChange = new EventEmitter<IDrivingStyle>();
    @Output() validate = new EventEmitter<boolean>();

    @ViewChild("groupsAutocomplete", { static: false }) groupsAutocomplete: VAutocompleteComponent<IGroup>;

    groups: IGroup[];
    depotGroups: IGroup[];
    vehicleTypeKVPs: IKeyValuePair<VehicleType>[];

    remainingAllowedGroups: IGroup[];
    remainingAllowedVehicleTypes: any[];

    loading: boolean;
    showCreateSidebar: boolean;

    selectionType: SelectionType = SelectionType.Group;

    SelectionType = SelectionType;
    VehicleType = VehicleType;

    existingAssociations: IDrivingStyleAssociation[];
    selectedGroupId: number;
    selectedVehicleTypeKVP: IKeyValuePair<VehicleType>;
    hasAllResourcesAssociation: boolean;
    showVehicleTypeSelect: boolean;

    associationForm: FormGroup;

    get groupIdControl(): AbstractControl {
        return this.associationForm?.get("GroupId");
    }

    get vehicleTypeControl(): AbstractControl {
        return this.associationForm?.get("VehicleType");
    }

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly groupService: GroupService,
        private readonly vehicleService: VehicleService,
        private readonly drivingStylesService: DrivingStylesService,
    ) {

    }

    ngOnInit(): void {
        this.prepare();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.prepare();
    }

    addAssociation(): void {
        const newAssociation = this.createAssociationModel();

        newAssociation.GroupId = this.selectedGroupId;
        newAssociation.VehicleType = (this.selectedVehicleTypeKVP?.value === -1) ? null : this.selectedVehicleTypeKVP?.value;

        this.drivingStyle.DrivingStyleAssociations.push(newAssociation);
        this.updatePills();

        this.selectedGroupId = null;
        this.groupsAutocomplete.clearInput();
        this.setDefaultVehicleType()

        this.resetForm();
        this.emitChange();
    }

    onApplyToAllResourcesChanged(value: boolean): void {
        this.drivingStyle.DrivingStyleAssociations = [];

        if (value) {
            this.drivingStyle.DrivingStyleAssociations.push(this.createAllResourcesAssociation());
        }

        this.setDefaultVehicleType();
        this.updatePills();
        this.onGroupCleared();
        this.emitChange();
    }

    onVehicleTypeSelectChange(vehicleTypeKVP: IKeyValuePair<VehicleType>): void {
        this.selectedVehicleTypeKVP = vehicleTypeKVP;
        this.vehicleTypeControl.setValue(vehicleTypeKVP.value);
        this.validateForm();
    }

    onGroupSelected(group: IGroup): void {
        this.selectedGroupId = group.Id;
        this.groupIdControl.setValue(group.Id);
        this.validateForm();
    }

    onGroupCleared(): void {
        this.selectedGroupId = null;
        this.groupIdControl.setValue(null);
        this.validateForm();
    }

    removeAssociation(associationToRemove: IDrivingStyleAssociation): void {
        const matchingAssociations = this.drivingStyle.DrivingStyleAssociations.filter(association => {
            return association.GroupId === associationToRemove.GroupId
                && association.VehicleType === associationToRemove.VehicleType
        });

        if (!matchingAssociations?.length) {
            return;
        }

        matchingAssociations.forEach(matchingAssociation => {
            this.drivingStyle.DrivingStyleAssociations.splice(this.drivingStyle.DrivingStyleAssociations.indexOf(matchingAssociation), 1);
        })

        this.updatePills();
        this.emitChange();
    }

    groupFilter = (group: IGroup, text: string): boolean => {
        return this.groupFilterSearchPredicate(group, text);
    }

    groupFilterSearchPredicate(group: IGroup, text: string): boolean {
        const alreadyAdded: boolean = !!this.drivingStyle.DrivingStyleAssociations.find(association => association.GroupId === group.Id);
        const matchesName: boolean = group.Name.toLowerCase().includes(text.toLowerCase());
        const isDepot: boolean = group.IsDepot;

        return !alreadyAdded && matchesName && isDepot;
    }

    onPillRemoveClick(pill: IPill<IDrivingStyleAssociation>): void {
        const matchingAssociation = this.drivingStyle.DrivingStyleAssociations.find(association => association === pill.data);

        if (!matchingAssociation) {
            return;
        }

        this.drivingStyle.DrivingStyleAssociations.splice(this.drivingStyle.DrivingStyleAssociations.indexOf(matchingAssociation), 1);

        this.updatePills();
    }

    //--

    private prepare(): void {

        this.existingAssociations = this.existingDrivingStyles.map(drivingStyle => drivingStyle.DrivingStyleAssociations).flat(1);

        this.getData().finally(() => {
            this.updatePills();

            this.setDefaultVehicleType();
            this.determineAllResourcesAssociation();
            this.setupForm();
        });

    }

    private setupForm(): void {
        this.associationForm = this.formBuilder.group({
            GroupId: ["", [this.validateAssociationField()]],
            VehicleType: [-1, [this.validateAssociationField()]]
        });

        this.validate.emit(this.associationForm.valid);
    }

    private validateAssociationField(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.associationForm) {
                return null;
            }

            const groupIdValue: number = (!!this.groupIdControl?.value) ? this.groupIdControl.value : null;
            const vehicleTypeValue: number = (this.vehicleTypeControl?.value > -1) ? this.vehicleTypeControl.value : null;

            const existingMatchingAssociation = this.drivingStyle.DrivingStyleAssociations?.find(association => {
                return association.GroupId == groupIdValue && association.VehicleType == vehicleTypeValue && this.associationHasValues(association);
            });

            if (existingMatchingAssociation) {
                return { customMessage: `An association matching your selection already exists below` }
            }

            const existingMatchingAssociationInOtherStyles = this.existingAssociations.find(association => {
                return association.DrivingStyleId !== this.drivingStyle.Id
                    && (association.GroupId == groupIdValue && association.VehicleType == vehicleTypeValue && this.associationHasValues(association)
                    || (this.hasAllResourcesAssociation && !this.associationHasValues(association)));
            });

            if (existingMatchingAssociationInOtherStyles) {
                const styleName = this.existingDrivingStyles.find(drivingStyle => drivingStyle.Id === existingMatchingAssociationInOtherStyles.DrivingStyleId)?.Name ?? "Unknown";

                return { customMessage: `An association matching your selection already exists in another driving style called "${styleName}"` }
            }

            return null;
        };
    }

    private associationHasValues(association: IDrivingStyleAssociation): boolean {
        return (!!association.GroupId && association.GroupId !== 0) || !!VehicleType[association.VehicleType] && association.VehicleType !== -1;
    }

    private validateForm(): void {
        this.vehicleTypeControl.updateValueAndValidity();
        this.groupIdControl.updateValueAndValidity();
    }

    private resetForm(): void {
        this.groupIdControl.setValue(null);
        this.vehicleTypeControl.setValue(null);
        this.validateForm();
    }

    private createAssociationModel(): IDrivingStyleAssociation {
        const newAssociation = this.drivingStylesService.createDefaultDrivingStyleAssociation();

        newAssociation.AccountId = this.drivingStyle.AccountId;
        newAssociation.DrivingStyleId = this.drivingStyle.Id;

        return newAssociation;
    }

    private emitChange(): void {
        this.drivingStyleChange.emit(this.drivingStyle);
        this.validate.emit(this.associationForm.valid);
    }

    private async getData(): Promise<void> {
        if (this.loading) {
            return;
        }

        this.loading = true;

        const promises: Promise<any>[] = [
            (this.groupService.getGroups() as Promise<IGroup[]>)
        ];

        await Promise.all(promises).then(([groups]) => {
            this.groups = groups;
            this.depotGroups = groups?.filter(group => group.IsDepot);
            this.vehicleTypeKVPs = this.filterAvailableVehicleTypes(this.vehicleService.getVehicleTypes());
        });

        this.loading = false;
    }

    private updatePills(): void {
        this.drivingStylesService.createAssociationPillsForDrivingStyle(this.drivingStyle, this.groups);
    }

    private filterAvailableVehicleTypes(vehicleTypes: IKeyValuePair<VehicleType>[]): IKeyValuePair<VehicleType>[] {
        const availableVehicleTypes: IKeyValuePair<VehicleType>[] = [];

        if (!vehicleTypes?.length) {
            return availableVehicleTypes;
        }

        vehicleTypes.forEach(vehicleType => availableVehicleTypes.push(vehicleType));

        availableVehicleTypes.sort((vehicleType1, vehicleType2) => {
            if (vehicleType1.value === VehicleType.None) {
                return -1
            }

            return (vehicleType1.displayName > vehicleType2.displayName) ? 1 : 0;
        });

        return availableVehicleTypes;
    }

    private setDefaultVehicleType(): void {
        if (!this.vehicleTypeKVPs?.length) {
            return;
        }

        this.showVehicleTypeSelect = false;

        this.selectedVehicleTypeKVP = this.vehicleTypeKVPs.find(vehicleTypeKVP => vehicleTypeKVP.value === VehicleType.None);

        setTimeout(() => {
            this.showVehicleTypeSelect = true;
        });
    }

    private determineAllResourcesAssociation(): void {
        this.hasAllResourcesAssociation = false;

        if (!this.drivingStyle.DrivingStyleAssociations?.length) {
            return;
        }

        const allResourcesAssociation = this.drivingStyle.DrivingStyleAssociations.find(association => !association.GroupId && !VehicleType[association.VehicleType] && association.VehicleType !== VehicleType.None);

        if (!allResourcesAssociation) {
            return;
        }

        this.hasAllResourcesAssociation = true;
    }

    private createAllResourcesAssociation(): IDrivingStyleAssociation {
        const newAssociation: IDrivingStyleAssociation = this.drivingStylesService.createDefaultDrivingStyleAssociation();

        newAssociation.DrivingStyleId = this.drivingStyle.Id;
        newAssociation.VehicleType = null;
        newAssociation.GroupId = null;

        return newAssociation;
    }
}
